import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Save, X, RefreshCw, Globe, Check, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { 
  fetchRssFeedSources, 
  addRssFeedSource, 
  updateRssFeedSource, 
  deleteRssFeedSource,
  RssFeedSource
} from '../lib/rss.service';

const RSSSourceManager: React.FC = () => {
  const [isAddingSource, setIsAddingSource] = useState(false);
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<RssFeedSource>>({
    name: '',
    url: '',
    category: '',
    description: '',
    is_active: true,
    fetch_frequency_minutes: 60
  });
  const queryClient = useQueryClient();

  // Fetch RSS feed sources
  const { 
    data: sources = [], 
    isLoading, 
    isError 
  } = useQuery({
    queryKey: ['rssFeedSources'],
    queryFn: fetchRssFeedSources
  });

  // Add source mutation
  const addSourceMutation = useMutation({
    mutationFn: addRssFeedSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rssFeedSources'] });
      toast.success('RSS feed source added successfully');
      resetForm();
    },
    onError: (error) => {
      toast.error(`Failed to add source: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Update source mutation
  const updateSourceMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<RssFeedSource> }) => 
      updateRssFeedSource(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rssFeedSources'] });
      toast.success('RSS feed source updated successfully');
      resetForm();
    },
    onError: (error) => {
      toast.error(`Failed to update source: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Delete source mutation
  const deleteSourceMutation = useMutation({
    mutationFn: deleteRssFeedSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rssFeedSources'] });
      toast.success('RSS feed source deleted successfully');
    },
    onError: (error) => {
      toast.error(`Failed to delete source: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      category: '',
      description: '',
      is_active: true,
      fetch_frequency_minutes: 60
    });
    setIsAddingSource(false);
    setEditingSourceId(null);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.url || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (editingSourceId) {
      updateSourceMutation.mutate({ 
        id: editingSourceId, 
        updates: formData 
      });
    } else {
      addSourceMutation.mutate(formData as Omit<RssFeedSource, 'id' | 'created_at' | 'updated_at'>);
    }
  };

  // Handle edit source
  const handleEditSource = (source: RssFeedSource) => {
    setFormData({
      name: source.name,
      url: source.url,
      category: source.category,
      description: source.description,
      is_active: source.is_active,
      fetch_frequency_minutes: source.fetch_frequency_minutes
    });
    setEditingSourceId(source.id);
    setIsAddingSource(true);
  };

  // Handle delete source
  const handleDeleteSource = (id: string) => {
    if (confirm('Are you sure you want to delete this RSS feed source?')) {
      deleteSourceMutation.mutate(id);
    }
  };

  // Group sources by category
  const sourcesByCategory = sources.reduce((acc, source) => {
    if (!acc[source.category]) {
      acc[source.category] = [];
    }
    acc[source.category].push(source);
    return acc;
  }, {} as Record<string, RssFeedSource[]>);

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">RSS Feed Sources</h2>
          <button
            onClick={() => setIsAddingSource(!isAddingSource)}
            className="btn btn-primary py-2"
          >
            {isAddingSource ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Source
              </>
            )}
          </button>
        </div>

        {/* Add/Edit Form */}
        {isAddingSource && (
          <div className="mb-8 bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium mb-4">
              {editingSourceId ? 'Edit RSS Feed Source' : 'Add New RSS Feed Source'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border rounded-md focus:ring-primary focus:border-primary"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border rounded-md focus:ring-primary focus:border-primary"
                    required
                    list="categories"
                  />
                  <datalist id="categories">
                    {Object.keys(sourcesByCategory).map(category => (
                      <option key={category} value={category} />
                    ))}
                  </datalist>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full border rounded-md focus:ring-primary focus:border-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border rounded-md focus:ring-primary focus:border-primary"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Refresh Frequency (minutes)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="1440"
                    value={formData.fetch_frequency_minutes}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      fetch_frequency_minutes: parseInt(e.target.value) 
                    })}
                    className="w-full border rounded-md focus:ring-primary focus:border-primary"
                  />
                </div>
                
                <div className="flex items-center">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-outline py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary py-2"
                  disabled={addSourceMutation.isPending || updateSourceMutation.isPending}
                >
                  {(addSourceMutation.isPending || updateSourceMutation.isPending) ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {editingSourceId ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingSourceId ? 'Update Source' : 'Add Source'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Sources List */}
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : isError ? (
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p>Failed to load RSS feed sources</p>
          </div>
        ) : sources.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Globe className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p>No RSS feed sources found</p>
            <button
              onClick={() => setIsAddingSource(true)}
              className="mt-4 btn btn-primary py-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Source
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(sourcesByCategory).map(([category, categorySources]) => (
              <div key={category}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">{category}</h3>
                <div className="space-y-4">
                  {categorySources.map((source) => (
                    <div 
                      key={source.id} 
                      className={`border rounded-lg p-4 ${
                        source.is_active ? 'border-gray-200' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center">
                            <h4 className="text-lg font-medium text-gray-900">{source.name}</h4>
                            {!source.is_active && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                                Inactive
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{source.description}</p>
                          <div className="flex items-center mt-2 text-sm text-gray-500">
                            <a 
                              href={source.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Globe className="h-4 w-4 mr-1" />
                              {source.url.length > 50 ? `${source.url.substring(0, 50)}...` : source.url}
                            </a>
                          </div>
                          <div className="flex items-center mt-2 text-xs text-gray-500">
                            <span>Refresh: {source.fetch_frequency_minutes} minutes</span>
                            {source.last_fetched_at && (
                              <>
                                <span className="mx-2">•</span>
                                <span>Last fetched: {new Date(source.last_fetched_at).toLocaleString()}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditSource(source)}
                            className="p-2 text-gray-500 hover:text-primary hover:bg-gray-100 rounded-full"
                            title="Edit source"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteSource(source.id)}
                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 rounded-full"
                            title="Delete source"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RSSSourceManager;